import React from "react";
import { Dialog, DialogContent, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import usePhotosDialogStyles from "./css/usePhotosDialogStyles";

interface AttachmentPreviewProps {
  open: boolean;
  onClose: () => void;
  file?: string; // base64 arba Blob URL
  description?: string; // komentaras
  fileType?: string; // pvz. ".PNG", ".JPG", ".PDF"
  recordType?: string | number; // 1 = failas (nuotrauka arba PDF), 4 = nuoroda
  link?: string; // jei recordType = 4
}

// Tikriname, ar base64 yra validus
const isBase64 = (str?: string): boolean => {
  if (!str) return false;
  return /^[A-Za-z0-9+/=]+$/.test(str);
};

// Tikriname, ar URL yra saugus
const isSafeUrl = (url?: string): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
};

// Leidžiami paveikslėlių tipai
const allowedImageTypes = ["jpg", "jpeg", "png"];

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  open,
  onClose,
  file,
  description,
  fileType,
  recordType,
  link,
}) => {
  const { classes } = usePhotosDialogStyles();

  const rt = String(recordType ?? "");

  const renderContent = () => {
    // 1) Failas (nuotrauka arba PDF)
    if (rt === "1" && fileType) {
      const lowerType = fileType.toLowerCase().replace(".", "");

      // --- Saugumo tikrinimai ---
      if (!isBase64(file)) {
        return (
          <Typography color="error">
            Neteisingas arba nesaugus failo formatas
          </Typography>
        );
      }

      // Nuotrauka
      if (allowedImageTypes.includes(lowerType)) {
        return (
          <img
            src={`data:image/${lowerType};base64,${file}`}
            alt="Attachment"
            style={{ width: "100%", height: "auto" }}
          />
        );
      }

      // PDF
      if (lowerType === "pdf") {
        const pdfUrl = `data:application/pdf;base64,${file}`;
        // Atidarome tik jei base64 validus
        window.open(pdfUrl, "_blank", "noopener,noreferrer");
        return <Typography>PDF atidaromas naujame lange</Typography>;
      }

      return (
        <Typography color="error">
          Neleistinas failo tipas: {fileType}
        </Typography>
      );
    }

    if (rt === "4" && link) {
      if (!isSafeUrl(link)) {
        return (
          <Typography color="error">Nesaugi arba neteisinga nuoroda</Typography>
        );
      }

      return (
        <a href={link} target="_blank" rel="noopener noreferrer">
          {description || "Atidaryti nuorodą"}
        </a>
      );
    }

    return <Typography color="error">Nepavyko atvaizduoti failo</Typography>;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className={classes.dialogRoot}
    >
      <div className={classes.header}>
        Failo peržiūra
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className={classes.previewContent}>
        {renderContent()}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {description || "Be komentaro"}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default AttachmentPreview;
